import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import {
  addXeroCorrespondenceNote,
  fetchXeroInvoiceDetail,
  formatXeroDate,
  fmtXeroMoney,
  sendXeroInvoiceReminder,
  type XeroCorrespondenceEntry,
  type XeroInvoiceDetail,
} from '../../services/xeroApi';
import { useAppSettings } from '../../context/SettingsContext';
import { wizzardGenerateReminder, isWizzardConfigured, type ReminderTone } from '../../services/wizzardApi';
import { XeroStatusChip } from './xeroShared';
import { WizzardToneBar } from './WizzardToneBar';
import { defaultReminderTone } from './reminderTones';

type Props = {
  invoiceId: string | null;
  open: boolean;
  onClose: () => void;
  onSent?: () => void;
  initialShowReminder?: boolean;
};

function directionLabel(d: string): string {
  if (d === 'out') {
    return 'Sent';
  }
  if (d === 'in') {
    return 'Reply';
  }
  return 'Note';
}

export function InvoiceDetailDialog({ invoiceId, open, onClose, onSent, initialShowReminder }: Props) {
  const { settings, isSecretConfigured } = useAppSettings();
  const collections = settings.integrations.xero.collections;
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<XeroInvoiceDetail | null>(null);
  const [correspondence, setCorrespondence] = useState<XeroCorrespondenceEntry[]>([]);
  const [showReminder, setShowReminder] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachPdf, setAttachPdf] = useState(true);
  const [stripeLink, setStripeLink] = useState(true);
  const [noteBody, setNoteBody] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [reminderTone, setReminderTone] = useState<ReminderTone>('friendly');
  const wizzardReady = isWizzardConfigured(settings, isSecretConfigured);

  const load = useCallback(async () => {
    if (!invoiceId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchXeroInvoiceDetail(invoiceId);
      setInvoice(data.invoice);
      setCorrespondence(data.correspondence || []);
      setReminderTone(defaultReminderTone(data.invoice.daysOverdue));
      setTo(data.invoice.contactEmail || '');
      setSubject(`Payment reminder — invoice ${data.invoice.number}`);
      setMessage('');
      setAttachPdf(collections?.attachPdf !== false);
      setStripeLink(collections?.stripePaymentLink !== false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, collections]);

  useEffect(() => {
    if (open && invoiceId) {
      void load();
      setShowReminder(Boolean(initialShowReminder));
      setShowNote(false);
    }
  }, [open, invoiceId, load, initialShowReminder]);

  const handleSend = async () => {
    if (!invoiceId) {
      return;
    }
    setSending(true);
    setError(null);
    try {
      await sendXeroInvoiceReminder(invoiceId, {
        to,
        subject,
        message: message || undefined,
        attachPdf,
        includeStripePaymentLink: stripeLink,
      });
      setShowReminder(false);
      await load();
      onSent?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleWizzardDraft = async (tone: ReminderTone = reminderTone) => {
    if (!invoiceId) {
      return;
    }
    setDrafting(true);
    setError(null);
    setReminderTone(tone);
    try {
      const draft = await wizzardGenerateReminder({ invoiceIds: [invoiceId], tone });
      setSubject(draft.subject);
      setMessage(draft.message);
      setShowReminder(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Wizzard draft failed');
    } finally {
      setDrafting(false);
    }
  };

  const handleAddNote = async () => {
    if (!invoiceId || !noteBody.trim()) {
      return;
    }
    setSending(true);
    try {
      await addXeroCorrespondenceNote(invoiceId, noteBody);
      setNoteBody('');
      setShowNote(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save note');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <span>{invoice ? `Invoice ${invoice.number}` : 'Invoice'}</span>
        {invoice?.xeroUrl && (
          <Button size="small" startIcon={<OpenInNewIcon />} href={invoice.xeroUrl} target="_blank">
            Open in Xero
          </Button>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box py={4} textAlign="center">
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {invoice && !loading && (
          <Stack spacing={2}>
            <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
              <XeroStatusChip row={invoice} />
              <Typography variant="body2" color="text.secondary">
                {invoice.contact}
              </Typography>
              {invoice.daysOverdue > 0 && (
                <Typography variant="body2" color="error.main" fontWeight={700}>
                  {invoice.daysOverdue} days overdue
                </Typography>
              )}
            </Stack>

            <Stack direction="row" flexWrap="wrap" gap={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Invoice date
                </Typography>
                <Typography fontWeight={600}>{formatXeroDate(invoice.date)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Due date
                </Typography>
                <Typography fontWeight={600}>{formatXeroDate(invoice.dueDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Amount due
                </Typography>
                <Typography fontWeight={800} color={invoice.amountDue > 0 ? 'error.main' : 'success.main'}>
                  {fmtXeroMoney(invoice.amountDue, invoice.currency)}
                </Typography>
              </Box>
            </Stack>

            <Divider />

            <Typography variant="subtitle2" fontWeight={700}>
              Line items
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.lineItems.map((li, i) => (
                  <TableRow key={i}>
                    <TableCell>{li.description}</TableCell>
                    <TableCell align="right">{fmtXeroMoney(li.lineAmount, invoice.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={700}>
                Correspondence trail
              </Typography>
              <Button size="small" startIcon={<NoteAddOutlinedIcon />} onClick={() => setShowNote((v) => !v)}>
                Log reply / call
              </Button>
            </Stack>
            {showNote && (
              <Stack spacing={1}>
                <TextField
                  label="Paste customer reply, call summary, or internal note"
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                />
                <Button size="small" variant="outlined" disabled={sending || !noteBody.trim()} onClick={() => void handleAddNote()}>
                  Save to trail
                </Button>
              </Stack>
            )}
            {correspondence.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No emails or notes yet. Sent reminders and manual notes appear here.
              </Typography>
            ) : (
              correspondence.map((c) => (
                <Box
                  key={c.id}
                  sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.5}>
                    <Chip size="small" label={directionLabel(c.direction)} />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(c.created_at).toLocaleString()}
                    </Typography>
                    {c.recipient && (
                      <Typography variant="caption">→ {c.recipient}</Typography>
                    )}
                    {c.attachment_count > 0 && (
                      <Typography variant="caption">📎 {c.attachment_count} PDF(s)</Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" fontWeight={600}>
                    {c.subject}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {c.body.length > 280 ? `${c.body.slice(0, 280)}…` : c.body}
                  </Typography>
                  {c.stripe_payment_url && (
                    <Link href={c.stripe_payment_url} target="_blank" variant="body2">
                      Stripe payment link
                    </Link>
                  )}
                  {c.reply_to && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      Reply-To: {c.reply_to}
                    </Typography>
                  )}
                </Box>
              ))
            )}

            {showReminder && invoice.amountDue > 0 && (
              <>
                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>
                  Send payment reminder
                </Typography>
                {wizzardReady && (
                  <WizzardToneBar
                    tone={reminderTone}
                    onToneChange={setReminderTone}
                    onDraft={(t) => void handleWizzardDraft(t)}
                    drafting={drafting}
                  />
                )}
                <TextField
                  label="To"
                  fullWidth
                  size="small"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  helperText={
                    !to.trim()
                      ? 'No email on this Xero contact — add a primary email or a contact person with “Include in emails” in Xero, or type an address here.'
                      : undefined
                  }
                />
                <TextField label="Subject" fullWidth size="small" value={subject} onChange={(e) => setSubject(e.target.value)} />
                <TextField
                  label="Message (optional)"
                  fullWidth
                  multiline
                  minRows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <FormControlLabel
                  control={<Checkbox checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />}
                  label="Attach invoice PDF"
                />
                <FormControlLabel
                  control={<Checkbox checked={stripeLink} onChange={(e) => setStripeLink(e.target.checked)} />}
                  label="Include Stripe card payment link"
                />
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {invoice && invoice.amountDue > 0 && !showReminder && (
          <>
            {wizzardReady && (
              <Button
                variant="outlined"
                startIcon={drafting ? <CircularProgress size={18} /> : <AutoFixHighIcon />}
                disabled={drafting}
                onClick={() => {
                  setShowReminder(true);
                  void handleWizzardDraft(reminderTone);
                }}
              >
                Wizzard draft
              </Button>
            )}
            <Button variant="contained" color="secondary" startIcon={<EmailOutlinedIcon />} onClick={() => setShowReminder(true)}>
              Send reminder
            </Button>
          </>
        )}
        {showReminder && (
          <Button variant="contained" disabled={sending || !to.trim()} onClick={() => void handleSend()}>
            {sending ? 'Sending…' : 'Send email'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
